import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { WEEKDAYS } from '../../../shared/constants/application';
import { CaregiverFields, ClientFields, ContactFields } from '../components/forms/ApplicationFields';
import { useAuth } from '../contexts/AuthContext';
import { hasCompletedOnboarding } from '../lib/onboarding';
import { updateUser } from '../lib/userApi';
import type { Address, CaregiverApplication, ClientApplication, UpdateUserRequest, WeeklyAvailability } from '../types/user';
import {
  dedupeValues,
  parseCommaSeparatedValues,
  validateApplicationForm,
} from '../utils/applicationValidation';

const text = (data: FormData, name: string): string => String(data.get(name) ?? '').trim();
const selected = (data: FormData, name: string): string[] => data.getAll(name).map(String);
const yes = (data: FormData, name: string): boolean => text(data, name) === 'yes';

function selectedWithOther(data: FormData, name: string, otherName: string): string[] {
  const values = selected(data, name);
  const selections = values.filter((value) => value !== 'Other');
  const otherValue = values.includes('Other') ? text(data, otherName) : '';
  return dedupeValues([...selections, otherValue]);
}

export default function Onboarding({ editMode = false }: { editMode?: boolean }): React.JSX.Element {
  const { user, refreshUser } = useAuth();
  const oidc = useOidcAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user) return <Navigate to="/login" replace />;
  if (!editMode && hasCompletedOnboarding(user)) return <Navigate to="/dashboard" replace />;
  if (editMode && (!user.application || user.application.kind !== user.role)) return <Navigate to="/dashboard" replace />;

  const isCaregiver = user.role === 'caregiver';
  const existingApplication = editMode ? user.application : undefined;
  const caregiverApplication = existingApplication?.kind === 'caregiver' ? existingApplication : undefined;
  const clientApplication = existingApplication?.kind === 'client' ? existingApplication : undefined;
  const title = editMode ? 'Edit profile' : isCaregiver ? 'Caregiver application' : 'Client care request';
  const description = editMode ? 'Update and resubmit your profile.' : 'Complete your profile to continue.';

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) return;
    const data = new FormData(event.currentTarget);
    const validationError = validateApplicationForm(data, isCaregiver);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError(null);

    const address: Address = {
      line1: text(data, 'line1'),
      line2: text(data, 'line2') || undefined,
      city: text(data, 'city'),
      state: text(data, 'state'),
      postalCode: text(data, 'postalCode'),
    };
    const common = {
      firstName: text(data, 'firstName'),
      lastName: text(data, 'lastName'),
      phone: text(data, 'phone'),
      email: text(data, 'email'),
      address,
    };

    const application: CaregiverApplication | ClientApplication = isCaregiver ? {
      kind: 'caregiver',
      ...common,
      middleName: text(data, 'middleName') || undefined,
      preferredName: text(data, 'preferredName') || undefined,
      dateOfBirth: text(data, 'dateOfBirth'),
      yearsExperience: Number(text(data, 'yearsExperience')),
      languages: selectedWithOther(data, 'languages', 'otherLanguage'),
      servicesOffered: selectedWithOther(data, 'servicesOffered', 'otherExpertise'),
      certifications: selectedWithOther(data, 'certifications', 'otherCertification'),
      caregivingApproach: text(data, 'caregivingApproach'),
      serviceAreas: [text(data, 'serviceAreas')],
      maximumTravelMiles: Number(text(data, 'maximumTravelMiles')),
      priorCareSettings: parseCommaSeparatedValues(text(data, 'priorCareSettings')),
      availability: Object.fromEntries(WEEKDAYS.map((day) => [day, text(data, `availability-${day}`)])) as WeeklyAvailability,
      serviceLimitations: text(data, 'serviceLimitations') || undefined,
      hasDriversLicense: yes(data, 'hasDriversLicense'),
      hasReliableTransportation: yes(data, 'hasReliableTransportation'),
      willingToTransport: yes(data, 'willingToTransport'),
      insured: yes(data, 'insured'),
      authorizedToWork: yes(data, 'authorizedToWork'),
      legallyPermitted: yes(data, 'legallyPermitted'),
      backgroundCheckConsent: yes(data, 'backgroundCheckConsent'),
      independentContractorAcknowledgment: yes(data, 'independentContractorAcknowledgment'),
      references: [1, 2, 3].map((number) => ({
        name: text(data, `reference${number}Name`),
        relationship: text(data, `reference${number}Relationship`),
        contact: text(data, `reference${number}Contact`),
      })),
      printedName: text(data, 'printedName'),
    } : {
      kind: 'client',
      ...common,
      careRecipientName: text(data, 'careRecipientName'),
      relationshipToRecipient: text(data, 'relationshipToRecipient'),
      requestedServices: parseCommaSeparatedValues(text(data, 'requestedServices')),
      careNotes: text(data, 'careNotes') || undefined,
    };

    try {
      const accessToken = oidc.user?.access_token;
      if (!accessToken) throw new Error('Your session has expired. Please sign in again.');
      await updateUser(accessToken, { application } satisfies UpdateUserRequest);
      await refreshUser();
      navigate('/dashboard', { replace: true });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to save application');
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="mx-auto max-w-3xl px-6 py-12">
      <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-7 shadow-lg">
        <h1 className="text-2xl font-bold text-mcc-darkest">{title}</h1>
        <p className="mt-1 text-sm text-slate-600">
          {description} Fields marked <span className="text-red-700">*</span> are required.
        </p>
        <ContactFields application={existingApplication} email={user.email} isCaregiver={isCaregiver} />
        {isCaregiver
          ? <CaregiverFields application={caregiverApplication} />
          : <ClientFields application={clientApplication} />}
        {error && <p className="mt-5 text-sm text-red-700" role="alert">{error}</p>}
        <button disabled={saving} className="mt-7 rounded-md bg-mcc-neutral px-5 py-2.5 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
          {saving ? 'Saving…' : editMode ? 'Save profile' : 'Submit application'}
        </button>
      </form>
    </main>
  );
}
